import { useMemo } from 'react';
import {
  addDays,
  endOfDay,
  format,
  isSameDay,
  isWithinInterval,
  set,
  startOfDay,
} from 'date-fns';
import { Closure, ScheduleWorkingHours } from '@/types';
import { getActiveWorkingHours, getTimeZoneAbbreviation } from '@/utils';

interface UseStoreHoursProps {
  workingHours?: ScheduleWorkingHours[] | null;
  gcWorkingHours?: ScheduleWorkingHours[] | null;
  closures?: Closure[] | null;
  timezone?: string | null;
}

interface StoreStatus {
  isStoreOpen: boolean;
  storeOpeningTime: Date | null;
  storeClosingTime: Date | null;
  isTemporarilyClosed: boolean;
  closureNote: { title?: string; message?: string } | null;
  storeStatusLabel: string | null;
}

const DAY_OF_WEEK_MAP: string[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const NO_SCHEDULE_AVAILABLE = 'Hours Not Available';

export const useStoreHours = ({
  workingHours,
  gcWorkingHours,
  closures,
  timezone = 'US/Pacific',
}: UseStoreHoursProps): StoreStatus => {
  const storeStatus = useMemo(() => {
    const tzAbbr = getTimeZoneAbbreviation(timezone ?? 'US/Pacific');
    const tzSuffix = tzAbbr ? ` ${tzAbbr}` : '';

    const getCurrentDateInStoreTimezone = (): Date => {
      const dateInTimezone = new Date().toLocaleString('en-US', {
        timeZone: timezone!,
      });
      return new Date(dateInTimezone);
    };

    const getScheduleDate = (baseDate: Date, timeStr: string): Date => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return set(new Date(baseDate), {
        hours,
        minutes,
        seconds: 0,
        milliseconds: 0,
      });
    };

    const parseDateFromClosure = (dateStr: string): Date => {
      const [month, day, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    };

    const now = getCurrentDateInStoreTimezone();

    const activeWorkingHours = getActiveWorkingHours({
      adminWorkingHours: gcWorkingHours,
      configWorkingHours: workingHours,
    });

    // Recursively finds the next available opening time, skipping over any closures.
    const findNextOpeningTime = (
      startTime?: Date,
      iteration = 0,
    ): Date | null => {
      if (!activeWorkingHours?.length || !startTime || iteration > 14)
        return null;

      const searchStartDate = startOfDay(startTime);
      const startIndex = searchStartDate.getDay();
      let nextRegularOpening: Date | null = null;

      for (let i = 0; i < 7; i++) {
        const currentDayIndex = (startIndex + i) % 7;
        const potentialSchedule = activeWorkingHours.find(
          el =>
            el?.day_of_the_week?.toLowerCase() ===
              DAY_OF_WEEK_MAP[currentDayIndex] && el.start,
        );

        if (potentialSchedule?.start) {
          const baseDateForSchedule = addDays(searchStartDate, i);
          const potentialOpeningTime = getScheduleDate(
            baseDateForSchedule,
            potentialSchedule.start,
          );

          if (potentialOpeningTime > startTime) {
            nextRegularOpening = potentialOpeningTime;
            break;
          }
        }
      }

      if (!nextRegularOpening) return null;

      // Check if the found opening time is blocked by a closure.
      if (closures && closures?.length > 0) {
        for (const closure of closures.filter(Boolean)) {
          if (!closure.start_date || !closure.end_date) continue;

          let closureStart, closureEnd;
          const startDate = parseDateFromClosure(closure.start_date);
          const endDate = parseDateFromClosure(closure.end_date);

          if (closure.all_day) {
            closureStart = startOfDay(startDate);
            closureEnd = endOfDay(endDate);
          } else if (closure.time_start && closure.time_end) {
            closureStart = getScheduleDate(startDate, closure.time_start);
            closureEnd = getScheduleDate(endDate, closure.time_end);
          } else continue;

          if (
            isWithinInterval(nextRegularOpening, {
              start: closureStart,
              end: closureEnd,
            })
          ) {
            // Search again, starting from the end of this blocking closure.
            return findNextOpeningTime(closureEnd, iteration + 1);
          }
        }
      }
      return nextRegularOpening;
    };

    // Determines if the store is *currently* within a temporary closure period.
    if (closures && closures.length > 0) {
      for (const closure of closures.filter(Boolean)) {
        if (!closure.start_date || !closure.end_date) continue;

        const startDate = parseDateFromClosure(closure.start_date);
        const endDate = parseDateFromClosure(closure.end_date);
        let closureStartDateTime, closureEndDateTime;

        if (closure.all_day) {
          closureStartDateTime = startOfDay(startDate);
          closureEndDateTime = endOfDay(endDate);
        } else if (closure.time_start && closure.time_end) {
          closureStartDateTime = getScheduleDate(startDate, closure.time_start);
          closureEndDateTime = getScheduleDate(endDate, closure.time_end);
        } else continue;

        if (
          isWithinInterval(now, {
            start: closureStartDateTime,
            end: closureEndDateTime,
          })
        ) {
          const nextOpeningAfterClosure =
            findNextOpeningTime(closureEndDateTime);
          let statusLabel: string | null = NO_SCHEDULE_AVAILABLE;
          const closureEndsToday = isSameDay(now, closureEndDateTime);

          if (closureEndsToday && !closure.all_day) {
            statusLabel = `until ${format(closureEndDateTime, 'p')}${tzSuffix}`;
          } else if (nextOpeningAfterClosure) {
            statusLabel = `until ${format(
              nextOpeningAfterClosure,
              'E, MMM d p',
            )}${tzSuffix}`;
          }

          return {
            isStoreOpen: false,
            storeOpeningTime: nextOpeningAfterClosure,
            storeClosingTime: null,
            isTemporarilyClosed: true,
            closureNote: { title: closure?.name, message: closure?.note },
            storeStatusLabel: statusLabel,
          };
        }
      }
    }

    // If not in a closure, calculate status based on the regular working hours.
    if (!activeWorkingHours?.length) {
      return {
        isStoreOpen: false,
        storeOpeningTime: null,
        storeClosingTime: null,
        isTemporarilyClosed: false,
        closureNote: null,
        storeStatusLabel: NO_SCHEDULE_AVAILABLE,
      };
    }

    const todaysDayString = DAY_OF_WEEK_MAP[now.getDay()];
    const todaysSchedule = activeWorkingHours?.find(
      schedule => schedule?.day_of_the_week?.toLowerCase() === todaysDayString,
    );
    const openingTimeToday = todaysSchedule?.start
      ? getScheduleDate(now, todaysSchedule.start)
      : null;
    let closingTimeToday = todaysSchedule?.end
      ? getScheduleDate(now, todaysSchedule.end)
      : null;

    if (
      openingTimeToday &&
      closingTimeToday &&
      closingTimeToday <= openingTimeToday
    ) {
      closingTimeToday = addDays(closingTimeToday, 1);
    }

    const isStoreOpen =
      openingTimeToday && closingTimeToday
        ? isWithinInterval(now, {
            start: openingTimeToday,
            end: closingTimeToday,
          })
        : false;
    let storeOpeningTime: Date | null = openingTimeToday;

    if (!isStoreOpen) {
      storeOpeningTime = findNextOpeningTime(now);
    }

    // Determines the final display label, checking for early closures.
    let finalStatusLabel: string | null = null;

    if (isStoreOpen && closingTimeToday) {
      let effectiveClosingTime = closingTimeToday;

      // Check if an upcoming closure will close the store earlier than usual.
      if (closures && closures.length > 0) {
        for (const closure of closures.filter(Boolean)) {
          if (closure.all_day || !closure.start_date || !closure.time_start)
            continue;
          const closureStartDate = parseDateFromClosure(closure.start_date);

          if (isSameDay(now, closureStartDate)) {
            const closureStartTime = getScheduleDate(
              closureStartDate,
              closure.time_start,
            );

            if (
              closureStartTime > now &&
              closureStartTime < effectiveClosingTime
            ) {
              effectiveClosingTime = closureStartTime;
            }
          }
        }
      }
      finalStatusLabel = `until ${format(
        effectiveClosingTime,
        'p',
      )}${tzSuffix}`;
    } else if (!isStoreOpen && storeOpeningTime) {
      finalStatusLabel = `until ${format(storeOpeningTime, 'p')}${tzSuffix}`;
    }

    return {
      isStoreOpen,
      storeOpeningTime,
      storeClosingTime: closingTimeToday,
      isTemporarilyClosed: false,
      closureNote: null,
      storeStatusLabel: finalStatusLabel,
    };
  }, [workingHours, gcWorkingHours, closures, timezone]);

  return { ...storeStatus };
};
